import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Loader2, Clock, CheckCircle, AlertTriangle, ExternalLink, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { getDSRs, createDSR, updateDSRStatus } from '@/services/dpdpa';

const REQUEST_TYPES = ['access', 'correction', 'erasure', 'grievance', 'nomination'];
const STATUS_OPTIONS = ['pending', 'in_progress', 'completed', 'rejected'];

const statusBadge = (status) => {
  const map = {
    pending: 'bg-amber-500/10 text-amber-700',
    in_progress: 'bg-blue-500/10 text-blue-700',
    completed: 'bg-emerald-500/10 text-emerald-700',
    rejected: 'bg-red-500/10 text-red-700',
  };
  return map[status] || 'bg-gray-500/10 text-gray-700';
};

const priorityBadge = (priority) => {
  return priority === 'high' ? 'bg-red-500/10 text-red-700' : 'bg-blue-500/10 text-blue-700';
};

export default function DSRPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showStatus, setShowStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [form, setForm] = useState({ request_type: 'access', principal_identifier: '', identifier_type: 'email', description: '' });
  const [statusForm, setStatusForm] = useState({ status: '', resolution_notes: '' });

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getDSRs(statusFilter || undefined, typeFilter || undefined);
      setRequests(res.requests || []);
    } catch (err) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleCreate = async () => {
    if (!form.principal_identifier) { toast.error('Principal identifier is required'); return; }
    try {
      setSaving(true);
      await createDSR(form);
      toast.success('Data subject request created');
      setShowCreate(false);
      setForm({ request_type: 'access', principal_identifier: '', identifier_type: 'email', description: '' });
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create request');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!statusForm.status) { toast.error('Select a status'); return; }
    try {
      setSaving(true);
      await updateDSRStatus(showStatus.id, statusForm);
      toast.success('Status updated');
      setShowStatus(null);
      setStatusForm({ status: '', resolution_notes: '' });
      fetchRequests();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="dsr-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Data Subject Requests</h1>
          <p className="text-sm opacity-60">Manage DSR per DPDPA Section 11-14 — 7 day SLA</p>
        </div>
        <div className="flex gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-32" data-testid="dsr-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {REQUEST_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32" data-testid="dsr-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setShowCreate(true)} data-testid="create-dsr-btn">
            <Plus className="w-4 h-4 mr-2" /> New Request
          </Button>
        </div>
      </div>

      {requests.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50"><p>No data subject requests found.</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <Card key={r.id} data-testid={`dsr-card-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold capitalize">{r.request_type} Request</h3>
                      <Badge className={statusBadge(r.status)}>{r.status.replace(/_/g, ' ')}</Badge>
                      <Badge className={priorityBadge(r.priority)}>{r.priority}</Badge>
                      {r.jira_ticket_id && (
                        <a href={r.jira_ticket_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1">
                          <Badge variant="outline" className="text-xs"><ExternalLink className="w-3 h-3 mr-1" />{r.jira_ticket_id}</Badge>
                        </a>
                      )}
                    </div>
                    <p className="text-sm opacity-60">From: <span className="font-medium">{r.principal_identifier}</span> ({r.identifier_type})</p>
                    {r.description && <p className="text-sm opacity-50 mt-1">{r.description}</p>}
                    <div className="flex gap-4 mt-2 text-xs opacity-50">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Created: {new Date(r.created_at).toLocaleDateString()}</span>
                      {r.due_date && <span>Due: {new Date(r.due_date).toLocaleDateString()}</span>}
                      {r.resolution_notes && <span>Notes: {r.resolution_notes}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {r.status !== 'completed' && r.status !== 'rejected' && (
                      <Button variant="outline" size="sm" onClick={() => { setShowStatus(r); setStatusForm({ status: '', resolution_notes: '' }); }} data-testid={`update-dsr-${r.id}`}>
                        Update Status
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create DSR Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-dsr-dialog">
          <DialogHeader><DialogTitle>New Data Subject Request</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Request Type</Label>
              <Select value={form.request_type} onValueChange={v => setForm(prev => ({ ...prev, request_type: v }))}>
                <SelectTrigger data-testid="dsr-request-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REQUEST_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data Principal Identifier</Label>
              <Input value={form.principal_identifier} onChange={e => setForm(prev => ({ ...prev, principal_identifier: e.target.value }))} placeholder="user@example.com" data-testid="dsr-principal-input" />
            </div>
            <div>
              <Label>Identifier Type</Label>
              <Select value={form.identifier_type} onValueChange={v => setForm(prev => ({ ...prev, identifier_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="aadhaar_virtual">Aadhaar Virtual ID</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Details about the request..." data-testid="dsr-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="submit-dsr-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={!!showStatus} onOpenChange={(v) => { if (!v) setShowStatus(null); }}>
        <DialogContent data-testid="update-dsr-status-dialog">
          <DialogHeader><DialogTitle>Update Request Status</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select value={statusForm.status} onValueChange={v => setStatusForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger data-testid="dsr-new-status"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Resolution Notes</Label>
              <Textarea value={statusForm.resolution_notes} onChange={e => setStatusForm(prev => ({ ...prev, resolution_notes: e.target.value }))} placeholder="Notes about the resolution..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatus(null)}>Cancel</Button>
            <Button onClick={handleStatusUpdate} disabled={saving} data-testid="confirm-status-update-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
